import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ProjectStatusModel from '../../models/ProjectStatusModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';

const ProjectStatusView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<ProjectStatusModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<ProjectStatusModel>({
        id: 0,
        name: '',
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let projectStatusId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        projectStatusId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (projectStatusId !== undefined) {
            DataService.getById<ProjectStatusModel>('api/v1/projectstatuses/', projectStatusId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [projectStatusId]);

    function submit() {
        if (formData) {
            DataService
                .update<ProjectStatusModel>(`api/v1/projectstatuses/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (projectStatusId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.ViewProjectStatus)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Project status' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditProjectStatus}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('name')} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default ProjectStatusView;